import React, { FormEvent, useMemo, useState } from 'react'
import { StyledInvoicesTable, StyledTable, StyledActionsContainer } from './styled'
import { ReactBaseElement } from '../../types/react'
import { getTableColumns, getTableData } from '../../utils/tables'
import Table from '../../components/organisms/Table'
import { useMutation, useQuery } from 'react-query'
import Spinner from '../../components/atoms/Spinner'
import Container from '../../components/atoms/Container'
import { StyledDescription } from '../../components/styled/Description'
import Input from '../../components/atoms/Input'
import { Icons, Role } from '../../types/enums'
import { formatCurrency } from '../../utils/currency'
import { getInvoices, updateInvoice, InvoiceResponse } from '../../services/invoices'
import { useTranslation } from '../../providers/I18n'
import { getInvoiceColumns } from '../../config/invoices'
import { TableSelectedData } from '../../types/tables'
import Heading from '../../components/atoms/Heading'
import Button from '../../components/molecules/Button'
import { useModal } from 'react-modal-hook'
import EditEstimatedPaymentDate from '../../modals/EditEstimatedPaymentDate'
import EditInvoiceDetails from '../../modals/EditInvoiceDetails'
import { useUser } from '../../providers/User'

type InvoicesTableProps = ReactBaseElement & {
  status?: string
}

function getStatusColor(status: string) {
  switch (status) {
    case 'analysis':
      return 'var(--color-red-2)'
    case 'pending':
      return 'var(--color-yellow-2)'
    case 'sent':
      return 'var(--color-blue-2)'
    case 'paid':
      return 'var(--color-green-2)'
    default:
      return 'var(--color-gray-2)'
  }
}

function parseInvoicesForTable(
  invoices: InvoiceResponse[],
  getTranslation: (key: string) => string
) {
  return invoices.map((invoice) => ({
    id: invoice._id,
    bank: invoice.credit?.bank?.name ?? '--',
    client: invoice.credit?.client?.name.toUpperCase() ?? '--',
    total: formatCurrency(invoice.total),
    totalNumber: invoice.total || 0,
    bankPayer: invoice.meta?.bankPayer || '--',
    bankCommissionPercentage: invoice.meta?.bankCommissionPercentage != null
    ? `${(invoice.meta.bankCommissionPercentage * 100).toFixed(2)}%`
    : '--',
    bankCommissionAmount: invoice.meta?.bankCommissionAmount != null
    ? formatCurrency(invoice.meta.bankCommissionAmount)
    : '--',
    bankCommissionAmountNumber: invoice.meta?.bankCommissionAmount || 0,
    invoiceNumber: invoice.meta?.invoiceNumber ?? '--',
    estimatedDate: invoice.meta?.estimatedPaymentDate
      ? new Date(invoice.meta.estimatedPaymentDate).toLocaleDateString()
      : '--',
    invoiceDate: invoice.meta?.invoiceDate
      ? new Date(invoice.meta.invoiceDate).toLocaleDateString()
      : '--',
    paymentDate: invoice.meta?.paymentDate
      ? new Date(invoice.meta.paymentDate).toLocaleDateString()
      : '--',
    markAsPaidDate: invoice.meta?.markAsPaidDate
      ? new Date(invoice.meta.markAsPaidDate).toLocaleDateString()
      : '--',
    status: {
      text: getTranslation(`invoices.${invoice.status}`),
      color: getStatusColor(invoice.status),
    }
  }))
}

const InvoicesTable: React.FC<InvoicesTableProps> = (props) => {
  const { getTranslation } = useTranslation()
  const { user } = useUser()
  const [search, setSearch] = useState('')
  const [selectedInvoices, setSelectedInvoices] = useState<TableSelectedData>()

  const { data: invoices, isFetching } = useQuery<InvoiceResponse[]>(
    ['getInvoices', props.status],
    () => getInvoices({ status: props.status }),
    {
      refetchOnWindowFocus: false,
    }
  )

  const { mutateAsync: updateInvoiceHandler, isLoading: isUpdatingInvoice } =
    useMutation(({ id, payload }: { id: string; payload: Parameters<typeof updateInvoice>[1] }) =>
      updateInvoice(id, payload)
    )

  const [showEstimatedDateModal, hideEstimatedDateModal] = useModal(
    ({ in: open }) => (
      <EditEstimatedPaymentDate
        open={open}
        onSubmit={async (dateValue) => {
          if (dateValue && selectedInvoices) {
            const updates = selectedInvoices.selectedRows.map((invoice) =>
              updateInvoiceHandler({
                id: invoice.id,
                payload: { estimatedPaymentDate: new Date(dateValue), status: "pending" } as any,
              })
            )
            await Promise.all(updates)
            setSelectedInvoices({
              allSelected: false,
              selectedRows: [],
              selectedCount: 0,
            })
            window.location.reload();
          }
        }}
        onClose={() => {
          hideEstimatedDateModal()
        }}
      />
    ),
    [selectedInvoices]
  )

  const [showInvoiceDetailsModal, hideInvoiceDetailsModal] = useModal(
    ({ in: open }) => (
      <EditInvoiceDetails
        open={open}
        onSubmit={async ({ invoiceNumber, paymentDate }) => {
          if (selectedInvoices) {
            const updates = selectedInvoices.selectedRows.map((invoice) =>
              updateInvoiceHandler({
                id: invoice.id,
                payload: {
                  invoiceNumber,
                  paymentDate: new Date(paymentDate),
                  invoiceDate: new Date(),
                  status: 'sent',
                } as any,
              })
            )
            await Promise.all(updates)
            setSelectedInvoices({
              allSelected: false,
              selectedRows: [],
              selectedCount: 0,
            })
            window.location.reload()
          }
        }}
        onClose={() => {
          hideInvoiceDetailsModal()
        }}
      />
    ),
    [selectedInvoices]
  )

  const columns = useMemo(() => getTableColumns(getInvoiceColumns(props.status)), [props.status])

  const filteredInvoices = useMemo(() => {
    if (!invoices) return []

    const parsed = parseInvoicesForTable(invoices, getTranslation!)
    if (!search) return parsed

    return parsed.filter((invoice) =>
      Object.values(invoice).some((value) =>
        String(value).toLowerCase().includes(search)
      )
    )
  }, [invoices, search])

  const tableData = useMemo(
    () => getTableData(columns, filteredInvoices),
    [columns, filteredInvoices]
  )

  const totalSelected = useMemo(() => {
    let sum = 0
    if (selectedInvoices && selectedInvoices.selectedCount > 0) {
      sum = selectedInvoices.selectedRows.reduce((total, current) => {
        return total + (parseFloat(current.totalNumber) || 0)
      }, 0)
    }
    return sum
  }, [selectedInvoices])

  const totalCommissionAmount = useMemo(() => {
    let sum = 0
    if (selectedInvoices && selectedInvoices.selectedCount > 0) {
      sum = selectedInvoices.selectedRows.reduce((total, current) => {
        return total + (parseFloat(current.bankCommissionAmountNumber) || 0)
      }, 0)
    }
    return sum
  }, [selectedInvoices])

  const handleSelect = (selected: TableSelectedData) => {
    setSelectedInvoices(selected)
  }

  const handleSearch = (event: FormEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value.toLowerCase())
  }

  const handleMarkAsPaid = async () => {
    if (selectedInvoices) {
      const updates = selectedInvoices.selectedRows.map((invoice) =>
        updateInvoiceHandler({
          id: invoice.id,
          payload: {
            markAsPaidDate: new Date(),
            status: 'paid',
          } as any,
        })
      )
      await Promise.all(updates)
      setSelectedInvoices({
        allSelected: false,
        selectedRows: [],
        selectedCount: 0,
      })
      window.location.reload()
    }
  }

  if (isFetching) {
    return (
      <Container align="center">
        <Spinner type="dot" />
        <StyledDescription>Cargando lista de facturas.</StyledDescription>
      </Container>
    )
  }

  const FILE_NAME = `facturas_${props.status}`
  return (
    <StyledInvoicesTable>
      <StyledTable>
        <Container direction="row">
          <Input
            placeholder="Buscar"
            onChange={handleSearch}
            preIcon={Icons.SEARCH}
            value={search}
          />
        </Container>
        <Heading type="3" color="var(--color-red-4)">
          Total Seleccionado: {formatCurrency(totalSelected)}
        </Heading>
        {[Role.Admin, Role.Director, Role.Finance].includes(user?.role as Role) && (
          <Heading type="3" color="var(--color-red-4)">
            Total de Comisiones Seleccionadas: {formatCurrency(totalCommissionAmount)}
          </Heading>
        )}
        <Table
          columns={columns}
          data={tableData}
          onClick={() => { console.log('Row clicked') }}
          onSelect={handleSelect}
          fileName={FILE_NAME}
        />
        {props.status === 'analysis' &&
          selectedInvoices &&
          selectedInvoices.selectedCount > 0 && (
            <StyledActionsContainer>
              <StyledDescription>
                Tienes seleccionadas{' '}
                <span>{selectedInvoices.selectedCount}</span> facturas.
              </StyledDescription>
              <Button
                onClick={showEstimatedDateModal}
                loading={isUpdatingInvoice}
              >
                Establecer Fecha Estimada de Pago
              </Button>
            </StyledActionsContainer>
          )}
        {props.status === 'pending' &&
          selectedInvoices &&
          selectedInvoices.selectedCount > 0 && (
            <StyledActionsContainer>
              <StyledDescription>
                Tienes seleccionadas{' '}
                <span>{selectedInvoices.selectedCount}</span> facturas.
              </StyledDescription>
              <Button
                onClick={showInvoiceDetailsModal}
                loading={isUpdatingInvoice}
              >
                Registrar Datos de Factura
              </Button>
            </StyledActionsContainer>
          )}
        {props.status === 'sent' &&
          selectedInvoices &&
          selectedInvoices.selectedCount > 0 && (
            <StyledActionsContainer>
              <StyledDescription>
                Tienes seleccionadas{' '}
                <span>{selectedInvoices.selectedCount}</span> facturas.
              </StyledDescription>
              <Button
                onClick={handleMarkAsPaid}
                loading={isUpdatingInvoice}
              >
                Marcar como Cobradas
              </Button>
            </StyledActionsContainer>
          )}
      </StyledTable>
    </StyledInvoicesTable>
  )
}

export default InvoicesTable
